import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ChevronLeft, ChevronRight,
  AlertOctagon, CreditCard, Inbox, CheckCircle2, XCircle,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminAds.css';

/**
 * iter297 — Phase 3 Part 2.
 *
 * Unified manual-payments admin view (Bankak / Manual flows).
 *
 *  • Lists `unlock_requests` (CV Pro PDF) AND `payment_requests` (coin packages
 *    / subscriptions) in one filterable list.
 *  • Admin can approve / reject every row with a reason (required on reject).
 *  • Double-tap / refresh cannot double-credit — backend uses an atomic state
 *    lock and writes an `admin_audit_logs` entry for every transition.
 *  • User's wallet balance updates from the same source used by the header,
 *    the Wallet page, and the CV page (iter294 invariant).
 */

const STATUS_CHIPS = [
  { id: 'pending',   color: 'yellow', en: 'Pending',  ar: 'قيد المراجعة' },
  { id: 'approved',  color: 'green',  en: 'Approved', ar: 'موافق عليه' },
  { id: 'rejected',  color: 'red',    en: 'Rejected', ar: 'مرفوض' },
  { id: 'all',       color: 'blue',   en: 'All',      ar: 'الكل' },
];

const PAGE_SIZE = 50;

function DecisionModal({ open, item, decision, isArabic, onCancel, onConfirm, working }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open, item?.request_id, decision]);
  if (!open || !item || !decision) return null;
  const isApprove = decision === 'approve';
  const reasonRequired = !isApprove; // reject must have reason
  const canSubmit = !working && (!reasonRequired || reason.trim().length > 0);
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="admin-payments-confirm-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 12,
      }}
    >
      <div
        className="admin-card"
        style={{ width: '100%', maxWidth: 480, padding: 18, background: '#fff', color: '#0f172a', boxShadow: '0 16px 40px rgba(0,0,0,0.2)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          {isApprove
            ? <CheckCircle2 size={18} color="#16a34a" />
            : <XCircle size={18} color="#dc2626" />
          }
          <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a', fontWeight: 700 }}>
            {isApprove
              ? (isArabic ? 'الموافقة على الدفعة' : 'Approve payment')
              : (isArabic ? 'رفض الدفعة' : 'Reject payment')}
          </h3>
        </div>
        <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.55, marginTop: 4 }}>
          {isApprove
            ? (isArabic
              ? 'سيتم منح الفائدة (عملات أو ميزة PDF) للمستخدم فوراً، ولا يمكن التراجع. يُسجَّل الإجراء في سجل المراجعة.'
              : 'The user will receive the benefit (coins or PDF feature) immediately. This action cannot be reverted and is saved to the audit log.')
            : (isArabic
              ? 'لن يحصل المستخدم على أي فائدة. السبب مطلوب وسيُسجَّل في سجل المراجعة.'
              : 'The user receives nothing. Reason is required and saved to the audit log.')}
        </p>

        <div style={{ marginTop: 10, padding: 10, background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#0f172a' }}>
          <div><strong>{isArabic ? 'المستخدم:' : 'User:'}</strong> {item.user_email || item.user_id}</div>
          <div><strong>{isArabic ? 'الرصيد الحالي:' : 'Current balance:'}</strong> {item.user_coins ?? '—'}</div>
          <div><strong>{isArabic ? 'الطلب:' : 'Request:'}</strong> {isArabic ? item.display_ar : item.display_en}</div>
          <div><strong>{isArabic ? 'المرجع:' : 'Reference:'}</strong> {item.reference_code || '—'}</div>
        </div>

        <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 600, color: '#0f172a' }} htmlFor="payment-decision-reason">
          {reasonRequired
            ? (isArabic ? 'سبب الرفض (مطلوب):' : 'Rejection reason (required):')
            : (isArabic ? 'ملاحظة (اختيارية):' : 'Note (optional):')}
        </label>
        <textarea
          id="payment-decision-reason"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isArabic ? 'اكتب السبب…' : 'Write the reason…'}
          data-testid="admin-payments-confirm-reason"
          className="admin-input"
          style={{ width: '100%', resize: 'vertical', color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: 8, fontSize: 13 }}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            className="admin-btn"
            onClick={onCancel}
            disabled={working}
            data-testid="admin-payments-confirm-cancel"
          >
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
          <button
            type="button"
            className={`admin-btn ${isApprove ? '' : 'danger'}`}
            onClick={() => onConfirm(reason.trim())}
            disabled={!canSubmit}
            data-testid="admin-payments-confirm-submit"
            style={isApprove
              ? { background: '#16a34a', color: '#fff', fontWeight: 700 }
              : { background: '#dc2626', color: '#fff', fontWeight: 700 }}
          >
            {working
              ? <><Loader2 className="animate-spin" size={14} /> {isArabic ? 'جارٍ التنفيذ…' : 'Working…'}</>
              : (isApprove ? (isArabic ? 'الموافقة' : 'Approve') : (isArabic ? 'رفض' : 'Reject'))}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPayments() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';

  const [statusFilter, setStatusFilter] = useState('pending');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, stats: { pending: 0, approved: 0, rejected: 0 } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // { item, decision }
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/payments/manual',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('status', statusFilter);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const apiBase = getApiBase();
      const resp = await fetch(`${apiBase}/api/admin/payments/manual?${params.toString()}`, {
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        setError({
          endpoint: '/api/admin/payments/manual',
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
        });
        return;
      }
      const json = await resp.json();
      setData(json);
    } catch (e) {
      setError({
        endpoint: '/api/admin/payments/manual',
        status: 0,
        message: (e && e.message) ? e.message : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  const requestDecision = (item, decision) => {
    setPending({ item, decision });
  };

  const submitDecision = async (reason) => {
    if (!pending) return;
    const { item, decision } = pending;
    setWorking(true);
    try {
      const r = await fetch(`${getApiBase()}/api/admin/payments/manual/decision`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: item.request_id,
          kind: item.kind,
          action: decision,
          reason: reason || '',
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        return;
      }
      if (j.already_processed) {
        toast.info(isArabic ? 'تمت معالجة الطلب مسبقاً' : 'Request already processed');
      } else if (decision === 'approve') {
        const credited = j.coins_credited;
        toast.success(
          credited
            ? (isArabic ? `تمت الموافقة. أضيف ${credited} عملة للمحفظة.` : `Approved. Added ${credited} coins to wallet.`)
            : (isArabic ? 'تمت الموافقة وتم منح الفائدة.' : 'Approved. Benefit granted.')
        );
      } else {
        toast.success(isArabic ? 'تم رفض الطلب.' : 'Request rejected.');
      }
      setPending(null);
      await load();
    } catch (_e) {
      toast.error(isArabic ? 'حدث خطأ في الشبكة' : 'Network error');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div data-testid="admin-payments">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-payments-title">
            <CreditCard size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
            {isArabic ? 'المدفوعات اليدوية' : 'Manual Payments'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'طلبات الدفع اليدوي (بانكك) للعملات وميزة Pro. كل إجراء يتطلب تأكيداً ويُسجَّل في سجل المراجعة. الموافقة تُضيف العملات للمحفظة مرة واحدة فقط.'
              : 'Manual (Bankak) payment requests for coins and Pro features. Every action requires confirmation and is recorded in the audit log. Approving credits coins to the wallet exactly once.'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-payments-refresh">
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-ads-filters">
        <div className="admin-ads-status-filters" role="tablist">
          {STATUS_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`admin-pill ${statusFilter === c.id ? c.color : 'gray'}`}
              onClick={() => { setStatusFilter(c.id); setSkip(0); }}
              data-testid={`admin-payments-filter-${c.id}`}
              data-active={statusFilter === c.id ? 'true' : 'false'}
              aria-pressed={statusFilter === c.id}
              role="tab"
            >
              {isArabic ? c.ar : c.en}
              {c.id !== 'all' && (
                <span style={{ marginInlineStart: 6, opacity: 0.85, fontWeight: 700 }}>
                  ({data?.stats?.[c.id] ?? 0})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div
            className="admin-card"
            data-testid="admin-payments-error"
            style={{
              margin: 12,
              borderInlineStart: '3px solid #ef4444',
              background: 'rgba(239, 68, 68, 0.06)',
            }}
          >
            <h4 style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              <AlertOctagon size={14} />
              {isArabic ? 'تعذر تحميل المدفوعات' : 'Failed to load payments'}
            </h4>
            <ul style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, paddingInlineStart: 18 }}>
              <li>{isArabic ? 'النقطة' : 'Endpoint'}: <code>{error.endpoint}</code></li>
              <li>{isArabic ? 'حالة HTTP' : 'HTTP status'}: <strong>{error.status}</strong></li>
              <li>{isArabic ? 'التفاصيل' : 'Detail'}: <span>{error.message}</span></li>
            </ul>
            <button type="button" className="admin-btn" onClick={load} style={{ marginTop: 6 }} data-testid="admin-payments-error-retry">
              <RefreshCw size={14} /> {isArabic ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}

        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-payments-table">
            <thead>
              <tr>
                <th>{isArabic ? 'المستخدم' : 'User'}</th>
                <th>{isArabic ? 'الطلب' : 'Request'}</th>
                <th>{isArabic ? 'المبلغ' : 'Amount'}</th>
                <th>{isArabic ? 'المرجع' : 'Reference'}</th>
                <th>{isArabic ? 'الحالة' : 'Status'}</th>
                <th>{isArabic ? 'التاريخ' : 'Date'}</th>
                <th style={{ textAlign: 'end' }}>{isArabic ? 'إجراءات' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center' }}>
                  <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
                </td></tr>
              )}
              {!loading && (data.items || []).length === 0 && (
                <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-payments-empty">
                  <Inbox size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
                  <div>{isArabic ? 'لا توجد طلبات دفع بهذه الحالة.' : 'No payment requests match this status.'}</div>
                </td></tr>
              )}
              {!loading && (data.items || []).map((it) => {
                const status = it.payment_status || 'pending';
                const isPending = status === 'pending';
                return (
                  <tr key={`${it.kind}-${it.request_id}`} data-testid={`admin-payments-row-${it.request_id}`}>
                    <td>
                      <div className="admin-ads-owner">
                        <div>{it.user_name || '—'}</div>
                        <div className="admin-ads-owner-email">{it.user_email || ''}</div>
                        <div style={{ fontSize: 11, color: '#0ea5e9', marginTop: 2 }}>
                          {isArabic ? 'الرصيد: ' : 'Balance: '}<strong>{it.user_coins ?? '—'}</strong>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 12 }}>
                        <span className={`admin-pill ${it.kind === 'payment' ? 'blue' : 'yellow'}`} style={{ fontSize: 10 }}>
                          {it.kind === 'payment'
                            ? (isArabic ? 'عملات' : 'Coins')
                            : (isArabic ? 'CV Pro' : 'CV Pro')}
                        </span>
                        <div style={{ marginTop: 4 }}>{isArabic ? it.display_ar : it.display_en}</div>
                      </div>
                    </td>
                    <td>
                      {it.amount_coins > 0 && (
                        <div style={{ fontSize: 12 }}>
                          <strong>{it.amount_coins}</strong> {isArabic ? 'عملة' : 'coins'}
                        </div>
                      )}
                      {it.amount_sdg > 0 && (
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {it.amount_sdg} SDG
                        </div>
                      )}
                    </td>
                    <td>
                      <code style={{ fontSize: 11 }}>{it.reference_code || '—'}</code>
                      {it.receipt_image_url && (
                        <div style={{ marginTop: 4 }}>
                          <a href={it.receipt_image_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>
                            {isArabic ? 'الإيصال' : 'Receipt'}
                          </a>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`admin-pill ${
                        status === 'approved' ? 'green'
                          : status === 'rejected' ? 'red'
                          : 'yellow'
                      }`} data-testid={`admin-payments-status-${it.request_id}`}>
                        {status}
                      </span>
                      {it.admin_note && (
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, maxWidth: 160 }}>
                          {it.admin_note}
                        </div>
                      )}
                      {it.verified_by_email && (
                        <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                          {isArabic ? 'بواسطة:' : 'by:'} {it.verified_by_email}
                        </div>
                      )}
                    </td>
                    <td className="admin-ads-date">
                      {it.created_at ? new Date(it.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div className="admin-ads-actions" style={{ gap: 4 }}>
                        <button
                          type="button"
                          className="admin-btn"
                          onClick={() => requestDecision(it, 'approve')}
                          disabled={!isPending}
                          data-testid={`admin-payments-approve-${it.request_id}`}
                          style={isPending ? { background: '#16a34a', color: '#fff', fontWeight: 700, padding: '4px 8px', fontSize: 11 } : { padding: '4px 8px', fontSize: 11 }}
                        >
                          <CheckCircle2 size={12} />
                          <span style={{ marginInlineStart: 4 }}>{isArabic ? 'موافقة' : 'Approve'}</span>
                        </button>
                        <button
                          type="button"
                          className="admin-btn danger"
                          onClick={() => requestDecision(it, 'reject')}
                          disabled={!isPending}
                          data-testid={`admin-payments-reject-${it.request_id}`}
                          style={{ padding: '4px 8px', fontSize: 11 }}
                        >
                          <XCircle size={12} />
                          <span style={{ marginInlineStart: 4 }}>{isArabic ? 'رفض' : 'Reject'}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-payments-total">
            {isArabic ? `الإجمالي: ${data.total}` : `Total: ${data.total}`}
          </span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="admin-payments-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label" data-testid="admin-payments-page-label">
              {currentPage} / {pages}
            </span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="admin-payments-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <DecisionModal
        open={!!pending}
        item={pending?.item}
        decision={pending?.decision}
        isArabic={isArabic}
        working={working}
        onCancel={() => !working && setPending(null)}
        onConfirm={submitDecision}
      />
    </div>
  );
}
